// Sub-position classifier — names the cursor's fine-grained sub-position so the
// resolver can emit only the grammatically-legal continuation keywords (see
// `legalNextKeywords.ts`). PURE text scan — no parse, no async, safe on the
// resolver hot path.
//
// CONFIDENT-ONLY contract: every branch returns `null` unless the text scan is
// clearly at the named boundary. The resolver falls back to today's full-list
// emission on `null`, so we never silently drop a valid keyword at a spot we
// cannot classify reliably (the predicate/group/order-by slots lean hardest on
// this).
//
// Design: `docs/superpowers/specs/2026-06-14-legal-next-keyword-autocomplete-design.md` §5.3.

import type { AliasBinding, CompletionPositionKind } from '@/core/engines';

import { isAfterCompletedFromRelation } from './isAfterCompletedFromRelation';
import { isAfterCompletedSelectProjection } from './isAfterCompletedSelectProjection';
import { isAtJoinTargetSlot } from './isAtJoinTargetSlot';
import {
  SHAPE_POSITION_KINDS,
  classifyCaseSlot,
  classifyCteBodySlot,
  classifyExistsOpenSlot,
  classifyInsertShapeSlot,
  classifyKeywordHeadSlot,
  classifyUpdateTargetSlot,
  classifyWindowPartitionSlot,
  classifyWindowSpecSlot,
  isInsideNonSubqueryParen,
  isInsideOpenCase,
  isInsideWindowDefinition,
} from './clauseShapeSlot';
import { classifyDdlShapeSlot } from './ddlShapeSlot';
import { getLegalNextKeywords, isRowLimitSlot, type NextKeywordSlot } from './legalNextKeywords';
import { classifyPredicateTail } from './predicateTailSlot';
import { classifyRowLimit } from './rowLimitSlot';
import {
  classifyAggregateConstructSlot,
  classifyCommentOnSlot,
  classifyGrantObjectSlot,
  classifyHintParenSlot,
  classifyMergeSlot,
  classifyShowSlot,
  classifyStatementHeadSlot,
  classifyUpsertSlot,
  classifyVocabularySlot,
  isGrantStatement,
  mergePredicateTailSlot,
} from './statementShapeSlot';
import {
  IDENT_CHAR,
  barewordTokens,
  hasTrailingWhitespace,
  lastTokenEnd,
  scanOptionsFor,
  scanRegion,
  wordBefore,
  trailingWord,
  type ScanOptions,
} from './slotTextScan';

export type NextKeywordSlotResult = {
  readonly slot: NextKeywordSlot;
  readonly relationHasAlias: boolean;
  readonly projectionIsStar: boolean;
};

/** Introducer keywords that precede a relation in the FROM/JOIN list — when one
 *  of these is the token before the trailing identifier, that identifier is the
 *  relation itself (not an alias). */
const RELATION_INTRODUCERS: ReadonlySet<string> = new Set([
  'FROM',
  'JOIN',
  'APPLY',
  'LATERAL',
  'USING',
  'AS',
]);

/** Upper-cased keywords that can legally FOLLOW a completed relation — so when
 *  one of them is the trailing word it is a continuation keyword lexed as an
 *  identifier (e.g. `FROM orders natural` → `NATURAL` starts `NATURAL JOIN`),
 *  NOT an alias. Built from the canonical `getLegalNextKeywords` model (union
 *  across all engines for every after-relation / join-target / clause slot) so
 *  it stays in lock-step with the legal-keyword data rather than hand-rolled,
 *  plus the relation introducers / join-family modifiers that introduce or
 *  qualify the next relation (`INNER`/`LEFT`/`OUTER`/`ON`/`USING`/…). */
const RELATION_CONTINUATION_KEYWORDS: ReadonlySet<string> = (() => {
  const set = new Set<string>(RELATION_INTRODUCERS);
  // Join-family modifiers + ON/USING (the join clause continuations).
  for (const kw of ['INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'OUTER', 'NATURAL', 'ON', 'STRAIGHT_JOIN']) {
    set.add(kw);
  }
  // Every legal continuation keyword the model emits after a relation / join
  // target, across all engines (WHERE/GROUP/ORDER/HAVING/UNION/LIMIT/FETCH/
  // OFFSET/FOR/PIVOT/USE/FORCE/IGNORE/…). Two-word entries (`NULLS FIRST`) are
  // split so the leading token is excluded too.
  const engines: readonly EngineLike[] = ['mssql', 'pgsql', 'mysql', 'oracle', 'sqlite'];
  const slots: readonly NextKeywordSlot[] = [
    'after-from-relation-plain',
    'after-join-target',
    'after-on-predicate',
  ];
  for (const engine of engines) {
    for (const slot of slots) {
      for (const kw of getLegalNextKeywords(engine, slot)) {
        for (const part of kw.split(/\s+/)) set.add(part.toUpperCase());
      }
    }
  }
  return set;
})();

type EngineLike = 'mssql' | 'pgsql' | 'mysql' | 'oracle' | 'sqlite';

/**
 * Classify the cursor's sub-position, with TYPED-PREFIX PARITY.
 *
 * Every scan below reads the token BEFORE the cursor, and each one declines
 * when the cursor is attached to a bare word — the caret is then mid-token,
 * which for a relation / column position is exactly right (`FROM cus▮` wants
 * tables, not keywords). But it is wrong when the half-typed word IS the
 * continuation keyword the slot exists to offer: `FROM rental r ▮` names
 * `after-from-relation-plain` and offers `FORCE` / `USE` / `IGNORE`, while
 * `FROM rental r FOR▮` declined and the resolver fell back to the table +
 * database list — the very syntax error the slot was added to prevent
 * (mysql coverage script.22 §1).
 *
 * So on a decline we retry once with the attached word dropped and accept the
 * bare answer ONLY when that word is a prefix of one of the named slot's own
 * legal keywords. That guard is what keeps identifier positions untouched: a
 * half-typed table or alias (`FROM ren▮`) either lands on a caret the bare
 * scan also declines, or prefixes no keyword of the slot, and the classifier
 * still returns null.
 */
export function classifyNextKeywordSlot(
  text: string,
  offset: number,
  statementStart: number,
  positionKind: CompletionPositionKind,
  aliasBindings: readonly AliasBinding[],
  // Required: the text scans below lex `#` and `$tag$` per dialect, and a
  // caller that forgot to say which engine would silently read a MySQL comment
  // as code. See `scanOptionsFor`.
  engineId: string,
): NextKeywordSlotResult | null {
  const direct = classifySlotAt(
    text, offset, statementStart, positionKind, aliasBindings, engineId,
  );
  if (direct) return direct;
  const attached = trailingWord(text, Math.min(offset, text.length), statementStart);
  if (!attached) return null;
  const retry = classifySlotAt(
    text, attached.start, statementStart, positionKind, aliasBindings, engineId,
  );
  if (!retry) return null;
  return prefixesLegalKeyword(engineId, retry.slot, attached.word) ? retry : null;
}

/** True when `partial` is a prefix of any keyword `slot` legally offers on
 *  `engineId` (first word of a multi-word entry included — `NULLS FIRST` is
 *  offered as one item but typed one word at a time). */
function prefixesLegalKeyword(
  engineId: string,
  slot: NextKeywordSlot,
  partial: string,
): boolean {
  const upperPartial = partial.toUpperCase();
  for (const kw of getLegalNextKeywords(engineId as never, slot)) {
    if (kw.toUpperCase().startsWith(upperPartial)) return true;
  }
  return false;
}

function classifySlotAt(
  text: string,
  offset: number,
  statementStart: number,
  positionKind: CompletionPositionKind,
  aliasBindings: readonly AliasBinding[],
  engineId: string,
): NextKeywordSlotResult | null {
  const opts = scanOptionsFor(engineId);
  // Expression- and statement-shape boundaries first: a CASE branch, an UPDATE
  // target, a closed INSERT column list or CTE body, a window partition item.
  // These cut across clauses — a CASE lives in the select list, a WHERE or an
  // ORDER BY alike — so they are tested before the clause dispatch, and each
  // declines (null) at any caret it cannot name confidently.
  const shapeSlot = classifyShape(text, offset, statementStart, positionKind, opts);
  // 'decline' means a shape IS open but the caret inside it has no confident
  // answer — the clause dispatch below must not run, because a half-written
  // CASE is not a completed projection or a completed predicate.
  if (shapeSlot === 'decline') return null;
  if (shapeSlot) {
    // A slot the engine has no vocabulary for is not a classification: the
    // dialect simply lacks the construct (`BEGIN ▮` opens a PL/SQL block in
    // Oracle, not a transaction). Naming it anyway would be equivalent — the
    // emitter falls back on an empty list — except that it reads as a
    // confident answer to every caller. Row-limit slots are exempt: an empty
    // list THERE is the answer (only an integer is legal at `LIMIT ▮`).
    if (
      !isRowLimitSlot(shapeSlot) &&
      getLegalNextKeywords(engineId as never, shapeSlot).length === 0
    ) {
      return null;
    }
    return { slot: shapeSlot, relationHasAlias: false, projectionIsStar: false };
  }

  switch (positionKind) {
    case 'select-list':
      return classifySelectList(text, offset, statementStart, opts);
    case 'from':
      return classifyFrom(text, offset, statementStart, aliasBindings, opts);
    case 'join-on': {
      // A MERGE's `ON` is not a join: `WHEN` follows it, and WHERE / JOIN do
      // not. A branch's own `AND <predicate>` takes `THEN`. Same
      // completed-predicate test, different continuation set.
      const tail =
        mergePredicateTailSlot(text, offset, statementStart, opts) ?? 'after-on-predicate';
      const slot = classifyPredicateTail(text, offset, statementStart, tail, opts);
      return slot ? { slot, relationHasAlias: false, projectionIsStar: false } : null;
    }
    case 'where': {
      const slot = classifyPredicateTail(text, offset, statementStart, 'after-where-predicate', opts);
      return slot ? { slot, relationHasAlias: false, projectionIsStar: false } : null;
    }
    case 'having': {
      const slot = classifyPredicateTail(text, offset, statementStart, 'after-having-predicate', opts);
      return slot ? { slot, relationHasAlias: false, projectionIsStar: false } : null;
    }
    case 'set':
      // A completed `SET col = value` — the same "operand after a comparison
      // operator" shape a WHERE predicate has, so it reuses that classifier.
      // Only for a real UPDATE statement: MySQL's `INSERT … SET a = 1` and a
      // MERGE branch's `THEN UPDATE SET a = 1` share the clause but take
      // entirely different continuations (`ON DUPLICATE KEY UPDATE`, `WHEN`),
      // and answering with the UPDATE tails there is worse than falling back.
      if (barewordTokens(text, statementStart, offset, opts)[0]?.word !== 'UPDATE') return null;
      {
      const slot = classifyPredicateTail(text, offset, statementStart, 'after-set-assignment', opts);
      return slot ? { slot, relationHasAlias: false, projectionIsStar: false } : null;
    }
    case 'group-by':
      return classifyGroupBy(text, offset, statementStart, opts);
    case 'order-by':
      return classifyOrderBy(text, offset, statementStart, opts);
    case 'limit-offset': {
      const slot = classifyRowLimit(text, offset, statementStart, opts);
      return slot ? { slot, relationHasAlias: false, projectionIsStar: false } : null;
    }
    default:
      return null;
  }
}

/** Runs the shape classifiers in `clauseShapeSlot.ts`, most specific first.
 *  Returns the first confident slot, or null so the clause dispatch runs. */
function classifyShape(
  text: string,
  offset: number,
  statementStart: number,
  positionKind: CompletionPositionKind,
  opts: ScanOptions,
): NextKeywordSlot | 'decline' | null {
  // A CASE can be open at any expression position, so it is never gated on the
  // clause. Its WHEN region reuses this file's completed-predicate classifier
  // so `CASE WHEN x IS ▮` refines exactly like `WHERE x IS ▮`.
  // Statement-specific ladders first — a MERGE branch or an upsert tail sits in
  // whatever clause the last keyword happened to set, so the clause dispatch
  // would answer them with that clause's continuations.
  const statementSlot =
    classifyMergeSlot(text, offset, statementStart, opts) ??
    classifyUpsertSlot(text, offset, statementStart, opts) ??
    classifyHintParenSlot(text, offset, statementStart, opts) ??
    classifyShowSlot(text, offset, statementStart, opts) ??
    classifyStatementHeadSlot(text, offset, statementStart, opts) ??
    classifyCommentOnSlot(text, offset, statementStart, opts) ??
    classifyGrantObjectSlot(text, offset, statementStart, opts) ??
    classifyExistsOpenSlot(text, offset, statementStart, opts) ??
    classifyDdlShapeSlot(text, offset, statementStart, opts) ??
    classifyAggregateConstructSlot(text, offset, statementStart, positionKind, opts) ??
    classifyVocabularySlot(text, offset, statementStart, positionKind, opts);
  if (statementSlot) return statementSlot;

  // The head of a multi-word construct (`UNION ▮`, `NULLS ▮`, `x NOT ▮`) is
  // checked first: those words otherwise read as a completed relation or list
  // item and get a confident, entirely illegal answer.
  const headSlot = classifyKeywordHeadSlot(text, offset, statementStart, positionKind, opts);
  if (headSlot) return headSlot;

  const caseSlot = classifyCaseSlot(
    text,
    offset,
    statementStart,
    (slot) => classifyPredicateTail(text, offset, statementStart, slot, opts),
    opts,
  );
  if (caseSlot) return caseSlot;
  // An OPEN CASE swallows the clause tails: `SELECT CASE WHEN a.id ▮` is not a
  // finished projection (it used to answer FROM / AS / INTO), and the same
  // holds for a CASE mid-predicate or mid-ORDER-BY.
  if (isInsideOpenCase(text, offset, statementStart, opts)) return 'decline';

  if (!SHAPE_POSITION_KINDS.has(positionKind)) return null;
  return (
    classifyWindowSpecSlot(text, offset, statementStart, opts) ??
    classifyWindowPartitionSlot(text, offset, statementStart, opts) ??
    classifyInsertShapeSlot(text, offset, statementStart, opts) ??
    classifyUpdateTargetSlot(text, offset, statementStart, opts) ??
    classifyCteBodySlot(text, offset, statementStart, opts)
  );
}

// ─── select-list ─────────────────────────────────────────────────────────────

function classifySelectList(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlotResult | null {
  if (!isAfterCompletedSelectProjection(text, offset, statementStart, opts)) return null;
  // The after-projection continuation (FROM / INTO / AS) is a TOP-LEVEL slot.
  // Inside an open paren — a window `OVER (PARTITION BY … |)`, a function-arg
  // list, or a derived-table/subquery — `isAfterCompletedSelectProjection` can
  // report true on a `… BY ` / `… , ` boundary, but a relation name is not the
  // legal next token there. Defer (null → resolver keeps emitting columns).
  if (openParenDepthAt(text, offset, statementStart) > 0) return null;
  return {
    slot: 'after-select-projection',
    projectionIsStar: projectionIsStarAt(text, offset, statementStart),
    relationHasAlias: false,
  };
}

/** Net unclosed `(` count between `statementStart` and the cursor. A positive
 *  value means the cursor sits inside an open parenthesis group. */
function openParenDepthAt(text: string, offset: number, statementStart: number): number {
  const end = Math.min(offset, text.length);
  let depth = 0;
  for (let i = statementStart; i < end; i += 1) {
    const ch = text[i] ?? '';
    if (ch === '(') depth += 1;
    else if (ch === ')' && depth > 0) depth -= 1;
  }
  return depth;
}

/** True when the current projection item (between SELECT / the last top-level
 *  comma and the cursor) is exactly `*` or `alias.*`. */
function projectionIsStarAt(text: string, offset: number, statementStart: number): boolean {
  const end = Math.min(offset, text.length);
  // Find the start of the current projection item: scan back to the last
  // top-level (paren-depth 0) comma, or to the end of the SELECT keyword.
  let depth = 0;
  let itemStart = statementStart;
  for (let i = end - 1; i >= statementStart; i -= 1) {
    const ch = text[i] ?? '';
    if (ch === ')') depth += 1;
    else if (ch === '(') {
      if (depth > 0) depth -= 1;
    } else if (ch === ',' && depth === 0) {
      itemStart = i + 1;
      break;
    }
  }
  let segment = text.slice(itemStart, end).trim();
  // Strip a leading SELECT / DISTINCT / ALL / TOP n preamble when the item is
  // the first projection (no preceding comma found).
  const preamble = /^(SELECT\s+)?(DISTINCT\s+|ALL\s+|UNIQUE\s+)?(TOP\s+\d+\s+(PERCENT\s+)?)?/i;
  segment = segment.replace(preamble, '').trim();
  return segment === '*' || /^[A-Za-z_][A-Za-z0-9_]*\.\*$/.test(segment);
}

// ─── from ────────────────────────────────────────────────────────────────────

function classifyFrom(
  text: string,
  offset: number,
  statementStart: number,
  aliasBindings: readonly AliasBinding[],
  opts: ScanOptions,
): NextKeywordSlotResult | null {
  // Bare JOIN target (`… JOIN |`) → expecting a relation, not keywords.
  if (isAtJoinTargetSlot(text, offset, statementStart)) return null;
  // Inside a non-subquery paren the relation tails are illegal — a `PIVOT (…)`
  // or a T-SQL hint list lives in a `from` clause but takes neither WHERE nor
  // JOIN. (A derived table / `IN (SELECT …)` is a subquery and stays eligible.)
  if (isInsideNonSubqueryParen(text, offset, statementStart, opts)) return null;
  // A privilege statement's object list is not a FROM list: its continuations
  // are the grantee clause, not WHERE / JOIN / GROUP BY.
  if (isGrantStatement(text, offset, statementStart, opts)) return null;
  // A trailing `ON` / `USING` OPENS a position rather than closing a relation —
  // `GRANT SELECT ON ▮` and `COMMENT ON ▮` want the object list, not the
  // after-relation continuations. (A join's `ON` yields clause 'join-on' and
  // never reaches here.)
  const tail = wordBefore(text, lastTokenEnd(text, offset, statementStart), statementStart);
  if (tail === 'ON' || tail === 'USING') return null;
  if (!isAfterCompletedFromRelation(text, offset, statementStart)) return null;

  const relEnd = lastTokenEnd(text, offset, statementStart);
  const introducer = relationIntroducer(text, relEnd, statementStart);
  const hasAlias = relationHasAliasAt(text, relEnd, statementStart, aliasBindings);

  // Pending ON: the relation was introduced by a plain `[INNER|LEFT|RIGHT|FULL]?
  // JOIN` (not CROSS/NATURAL JOIN, not APPLY) and there is no ON/USING token
  // between that JOIN introducer and the cursor.
  // A DELETE's relation takes none of the JOIN ladder — every dialect spells
  // multi-table deletion with `USING` (or T-SQL's second `FROM`), so the
  // SELECT tail's `JOIN` / `INNER` / `LEFT` / … were all illegal here.
  if (introducer !== 'pending-on' && isDeleteStatement(text, statementStart, offset, opts)) {
    return { slot: 'after-delete-relation', relationHasAlias: hasAlias, projectionIsStar: false };
  }

  const slot: NextKeywordSlot = introducer === 'pending-on'
    ? 'after-join-target'
    : 'after-from-relation-plain';

  return { slot, relationHasAlias: hasAlias, projectionIsStar: false };
}

/** True when the statement's leading verb is DELETE. Scans only up to the
 *  caret — the verb always precedes a relation tail, and the memoised region
 *  is then the one every other classifier on this keystroke already uses. */
function isDeleteStatement(
  text: string,
  statementStart: number,
  offset: number,
  opts: ScanOptions,
): boolean {
  return scanRegion(text, statementStart, offset, opts).tokens[0]?.word === 'DELETE';
}

type Introducer = 'pending-on' | 'plain';

/** Walks back from the completed relation's end to classify how it was
 *  introduced. Returns `'pending-on'` when it is a JOIN target still awaiting
 *  its ON/USING; `'plain'` otherwise (FROM / comma / CROSS|NATURAL JOIN /
 *  APPLY, or a JOIN whose ON/USING is already present). */
function relationIntroducer(text: string, relEnd: number, statementStart: number): Introducer {
  // Tokenize the region from statementStart..relEnd into upper-cased barewords
  // with their positions; the cursor's relation occupies the tail.
  const tokens = barewordTokens(text, statementStart, relEnd);
  // Scan forward to find the LAST `JOIN` whose target spans to relEnd with no
  // intervening ON/USING. We walk from the end: skip the trailing alias/relation
  // identifiers, then look for the introducer keyword.
  let sawOnOrUsing = false;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const w = tokens[i]!.word;
    if (w === 'ON' || w === 'USING') {
      sawOnOrUsing = true;
      continue;
    }
    if (w === 'JOIN') {
      if (sawOnOrUsing) return 'plain'; // join already closed
      // Look at the modifier immediately before JOIN.
      const prev = i > 0 ? tokens[i - 1]!.word : '';
      if (prev === 'CROSS' || prev === 'NATURAL') return 'plain';
      return 'pending-on';
    }
    if (w === 'APPLY') return 'plain';
    if (w === 'FROM') return 'plain';
  }
  return 'plain';
}

/** Detects whether the just-completed relation already carries an alias. The
 *  relation tail looks like `<rel> <alias>` or `<rel> AS <alias>` when aliased;
 *  `<rel>` alone (preceded by an introducer keyword) when not. Robust to
 *  schema-qualified names (`Person.Person e`). */
function relationHasAliasAt(
  text: string,
  relEnd: number,
  statementStart: number,
  aliasBindings: readonly AliasBinding[],
): boolean {
  // Extract the trailing identifier word (the alias candidate).
  const trailing = trailingWord(text, relEnd, statementStart);
  if (!trailing) {
    // Trailing token is a delimited identifier close (`]`/`"`/`` ` ``) — could
    // be the relation or its alias; fall back to alias-binding evidence below.
    return aliasBindings.length > 0 && lastTokenIsBoundAlias(text, relEnd, statementStart, aliasBindings);
  }

  // The trailing word is a continuation/relation keyword lexed as an identifier
  // (`FROM orders natural ` → `NATURAL` starts `NATURAL JOIN`, not an alias) —
  // it cannot be an alias. Without this guard the legal `AS` continuation would
  // be wrongly stripped (alias deemed already present).
  if (RELATION_CONTINUATION_KEYWORDS.has(trailing.word.toUpperCase())) return false;

  // The token immediately before the trailing word (skipping whitespace and a
  // possible `AS`).
  let before = trailing.start;
  // skip whitespace
  while (before > statementStart && /\s/.test(text[before - 1] ?? '')) before -= 1;
  const prevWord = trailingWord(text, before, statementStart);
  if (!prevWord) return false; // trailing word directly preceded by a delimiter

  if (prevWord.word.toUpperCase() === 'AS') {
    // `<rel> AS <alias>` → alias present.
    return true;
  }

  // If the previous token is an introducer keyword, the trailing word IS the
  // relation (no alias). Otherwise it's an alias following a relation name
  // (incl. the tail of a dotted/schema-qualified name).
  if (RELATION_INTRODUCERS.has(prevWord.word.toUpperCase())) return false;

  // The char immediately before prevWord must not be a `.` — if it is, prevWord
  // is just the last segment of a dotted relation name (`Person.Person`) and
  // we need to keep walking. But the presence of any non-introducer identifier
  // before the trailing word still means the trailing word is the alias.
  return true;
}

/** Whether the trailing delimited identifier matches a bound alias. */
function lastTokenIsBoundAlias(
  text: string,
  relEnd: number,
  statementStart: number,
  aliasBindings: readonly AliasBinding[],
): boolean {
  // Read back over a delimited identifier `[..]` / "..." / `..`.
  let i = relEnd;
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  const close = text[i - 1] ?? '';
  const open = close === ']' ? '[' : close;
  if (close !== ']' && close !== '"' && close !== '`') return false;
  let j = i - 1;
  while (j > statementStart && text[j - 1] !== open) j -= 1;
  const inner = text.slice(j, i - 1).toLowerCase();
  return aliasBindings.some((b) => b.alias.toLowerCase() === inner);
}

// ─── group-by ────────────────────────────────────────────────────────────────

function classifyGroupBy(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlotResult | null {
  if (!isAfterCompletedListItem(text, offset, statementStart, opts)) return null;
  return { slot: 'after-group-by-item', relationHasAlias: false, projectionIsStar: false };
}

// ─── order-by ────────────────────────────────────────────────────────────────

function classifyOrderBy(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions,
): NextKeywordSlotResult | null {
  // Direction detection needs whitespace after ASC/DESC (cursor not mid-word).
  if (hasTrailingWhitespace(text, offset, statementStart)) {
    const end = scanRegion(text, statementStart, offset, opts).lastCodeEnd;
    const w = trailingWord(text, end, statementStart);
    if (w && (w.word.toUpperCase() === 'ASC' || w.word.toUpperCase() === 'DESC')) {
      const dirSlot: NextKeywordSlot = isInsideWindowDefinition(text, offset, statementStart, opts)
        ? 'after-window-order-direction'
        : 'after-order-by-direction';
      return { slot: dirSlot, relationHasAlias: false, projectionIsStar: false };
    }
  }
  if (!isAfterCompletedListItem(text, offset, statementStart, opts)) return null;
  // `OVER (ORDER BY x ▮)` is a WINDOW ordering: a frame (`ROWS`/`RANGE`) may
  // follow, and the statement-level tails (LIMIT / OFFSET / FETCH) may not.
  const slot: NextKeywordSlot = isInsideWindowDefinition(text, offset, statementStart, opts)
    ? 'after-window-order-item'
    : 'after-order-by-item';
  return { slot, relationHasAlias: false, projectionIsStar: false };
}

/** Confident "after a completed GROUP/ORDER list item": the cursor sits after
 *  whitespace whose previous token is a finished operand (identifier / `)` /
 *  delimited-ident close), the item is NOT a lone clause keyword (`BY`), and the
 *  previous non-ws char is NOT a comma. */
export function isAfterCompletedListItem(
  text: string,
  offset: number,
  statementStart: number,
  opts: ScanOptions = {},
): boolean {
  if (!hasTrailingWhitespace(text, offset, statementStart)) return false;
  const scan = scanRegion(text, statementStart, offset, opts);
  const end = scan.lastCodeEnd;
  if (end <= statementStart) return false;
  const prev = scan.lastCodeChar;
  if (prev === ',') return false;
  if (prev === ')' || prev === ']' || prev === '"' || prev === '`') return true;
  if (IDENT_CHAR.test(prev)) {
    const w = trailingWord(text, end, statementStart);
    if (!w) return false;
    const up = w.word.toUpperCase();
    // `GROUP BY |` / `ORDER BY |` → still expecting the first item.
    if (up === 'BY' || up === 'GROUP' || up === 'ORDER') return false;
    // The first word of a two-word construct is not a completed item —
    // `ORDER BY x NULLS ▮` takes FIRST/LAST, `GROUP BY x WITH ▮` takes ROLLUP.
    // Those are named by `classifyKeywordHeadSlot`, which runs first; the
    // refusal here keeps them from ALSO looking like a finished list item.
    if (up === 'NULLS' || up === 'WITH') return false;
    return true;
  }
  return false;
}

// ─── shared pure helpers ─────────────────────────────────────────────────────
// (`hasTrailingWhitespace`, `lastTokenEnd`, `trailingWord`, `wordBefore` and
// `barewordTokens` live in `slotTextScan.ts` — shared with `clauseShapeSlot.ts`.)
